import { type UserMessage } from "@sendbird/chat/message";
import { type SendableMessageType } from "@sendbird/uikit-react/types/utils";
import { useSyncExternalStore } from "react";

type Listener = () => void;

interface ChatActionState {
  editingMessage: UserMessage | null;
  quoteMessage: SendableMessageType | null;
}

let state: ChatActionState = {
  editingMessage: null,
  quoteMessage: null,
};

const listeners = new Set<Listener>();

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getEditingMessageSnapshot(): UserMessage | null {
  return state.editingMessage;
}

function getQuoteMessageSnapshot(): SendableMessageType | null {
  return state.quoteMessage;
}

function notifyListeners(): void {
  listeners.forEach((listener) => {
    listener();
  });
}

function setEditingMessage(message: UserMessage | null): void {
  state = { ...state, editingMessage: message };
  notifyListeners();
}

function setQuoteMessage(message: SendableMessageType | null): void {
  state = { ...state, quoteMessage: message };
  notifyListeners();
}

export function useEditingMessage(): UserMessage | null {
  return useSyncExternalStore(subscribe, getEditingMessageSnapshot);
}

export function useSetEditingMessage(): (message: UserMessage | null) => void {
  return setEditingMessage;
}

export function useQuoteMessage(): SendableMessageType | null {
  return useSyncExternalStore(subscribe, getQuoteMessageSnapshot);
}

export function useSetQuoteMessage(): (message: SendableMessageType | null) => void {
  return setQuoteMessage;
}

// Keep old export for compatibility but it's no longer needed
export function EditMessageProvider({ children }: { children: React.ReactElement }) {
  return children;
}
